import CopyViewLinkButton from './CopyViewLinkButton';
import { MenuFoldOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { Breadcrumb, Button, Drawer, Empty, Space, Typography } from 'antd';
import { getCommunityReport } from '../utils/filerHandler';
import CommunityReport from './CommunityReport';
import './Community.css';

const { Text } = Typography;

function CommunityList({
  details,
  contextCommunities,
  triggerSignal,
  setSelectedGraph,
  filesReady,
  showCommunityList,
  setShowCommunityList,
  selectedCommunity,
  newCommunity,
}) {
  const [currentId, setCurrentId] = useState(-1);
  const [path, setPath] = useState([-1]);

  const data = useMemo(() => {
    if (!details || !details.communities) return [];
    return details.communities;
  }, [details]);

  const idMap = useMemo(() => {
    const map = {};
    data.forEach((community) => {
      map[community.community] = community;
    });
    return map;
  }, [data]);

  const contextObj = useMemo(() => {
    try {
      return contextCommunities.reduce((acc, communityRef) => {
        const community = idMap[communityRef.id];
        if (community) {
          acc.push({ community, weight: communityRef.weight });
        }
        return acc;
      }, []);
    } catch (error) {
      console.error('Error processing context communities:', error);
      return [];
    }
  }, [contextCommunities, idMap]);

  function getWeight(community) {
    return contextObj.find((item) => item.community.community === community.community)?.weight || 0;
  }

  const currentChildren = useMemo(() => {
    if (currentId === -1) {
      return data.filter((community) => community.level === 0);
    }
    if (currentId === -2) {
      return contextObj.map((item) => item.community);
    }
    return (idMap[currentId]?.children || []).map((childId) => idMap[childId]).filter(Boolean);
  }, [currentId, idMap, data, contextObj]);

  useEffect(() => {
    if (contextCommunities && contextCommunities.length > 0) {
      setCurrentId(-2);
      setPath([-1, -2]);
    }
  }, [contextCommunities, triggerSignal]);

  useEffect(() => {
    if (selectedCommunity != null) {
      setPath([-1, selectedCommunity]);
      setCurrentId(selectedCommunity);
    }
  }, [selectedCommunity, newCommunity]);

  useEffect(() => {
    const community = idMap[currentId];
    if (!community) {
      setSelectedGraph({ nodes: [], links: [] });
      return;
    }
    setSelectedGraph({
      nodes: community.entity_ids || [],
      links: community.relationship_ids || [],
    });
  }, [currentId, idMap, setSelectedGraph]);

  const navigateTo = (id) => {
    setPath((prev) => [...prev, id]);
    setCurrentId(id);
  };

  const goBack = () => {
    const nextPath = [...path];
    nextPath.pop();
    if (nextPath.length === 0) {
      setPath([-1]);
      setCurrentId(-1);
      return;
    }
    setPath(nextPath);
    setCurrentId(nextPath[nextPath.length - 1]);
  };

  const breadcrumbItems = path.map((id, index) => ({
    title: (
      <button
        type="button"
        className="community-breadcrumb-button"
        disabled={index === path.length - 1}
        onClick={() => {
          setPath(path.slice(0, index + 1));
          setCurrentId(id);
        }}
      >
        {id === -1 ? 'Root' : id === -2 ? 'Context Communities' : idMap[id]?.title || `Community ${id}`}
      </button>
    ),
  }));

  return (
    <Drawer
      title="Community explorer"
      closeIcon={<MenuFoldOutlined />}
      extra={<CopyViewLinkButton />}
      placement="left"
      open={filesReady && showCommunityList}
      onClose={() => setShowCommunityList(false)}
      width={420}
      className="community-drawer"
    >
      <Space orientation="vertical" size={16} style={{ width: '100%' }}>
        <Breadcrumb items={breadcrumbItems} />

        {currentId !== -1 ? (
          <Button onClick={goBack} style={{ width: 'fit-content' }}>
            Back
          </Button>
        ) : null}

        {currentId >= 0 ? <CommunityReport id={currentId} reports={details.community_reports} /> : null}

        <div>
          {currentChildren.length === 0 ? (
            <Empty description="No subcommunities." />
          ) : currentId === -2 ? (
            currentChildren
              .slice()
              .sort((a, b) => getWeight(b) - getWeight(a))
              .map((community) => (
                <div key={community.community} className="community-tab">
                  <Text type="secondary">Weight: {getWeight(community)}</Text>
                  <button type="button" className="community-title" onClick={() => navigateTo(community.community)}>
                    {getCommunityReport(community.community, details.community_reports)?.title || community.title}
                  </button>
                </div>
              ))
          ) : (
            currentChildren.map((community) => (
              <div key={community.community} className="community-tab">
                <Text type="secondary">{community.human_readable_id}</Text>
                <button type="button" className="community-title" onClick={() => navigateTo(community.community)}>
                  {getCommunityReport(community.community, details.community_reports)?.title || community.title}
                </button>
              </div>
            ))
          )}
        </div>
      </Space>
    </Drawer>
  );
}

export default CommunityList;
